import type { EngagementHubMeta, EngagementTeaserPublic } from './engagement';

export * from './Cms';
export * from './address';
export * from './analytics';
export * from './anomalyReport';
export * from './article';
export * from './leagueTable';
export * from './match';
export * from './matchChangeset';
export * from './team';
export * from './club-options';
export * from './competition';
export * from './sponsor';
export * from './tag';
export * from './contact';
export * from './socialNetwork';
export * from './navigation';
export * from './job';
export * from './settings';
export * from './user';
export * from './permissions';
export * from './capabilityMetadata';
export * from './fileManagerActions';
export * from './asset';
export * from './bugTracker';
export * from './tenant';
export * from './search';
export * from './mail';
export * from './faq';
export * from './event';
export * from './socketEvent';
export * from './liveFeed';
export type {
  IfasChangeSide,
  IfasMatchField,
  IfasFieldChange,
  IfasEventSnapshot,
  IfasPhaseSnapshot,
  IfasLineupSnapshot,
  IfasChanged,
  IfasDiffList,
  IfasEventField,
  IfasLineupField,
  IfasMatchChanges,
} from './ifasMatchChanges';
export type {
  IfasPullOutcome,
  IfasPullTriggeredBy,
  IfasPullListItem,
  IfasMatchSheetEvent,
  IfasMatchSheetPlayer,
  IfasMatchSheet,
  IfasPullDetail,
  IfasPullPage,
} from './ifasManualPull';
export * from './releaseNote';
export * from './video';
export * from './matchMedia';
export * from './player';
export * from './playerIdentity';
export * from './statsIngestion';
export type { FootballPlayer, FootballPlayerStats, CalculatedPlayerStats } from './footballPlayer';
export * from './footballPlayerProfile';
export * from './playerListQuery';
export * from './playerStatsPreset';
export * from './PageTemplate';
export * from './scheduler';
export * from './cronjob';
export * from './photoAlbum';
export * from './scheduledTask';
export * from './file';
export * from './folder';
export * from './folder-permissions';
export * from './fileManager';
export * from './ppv-video';
export * from './report';
export * from './seasonConfig';
export * from './kitDefaults';
export * from './kitGeometry';
export * from './matchSummary';
export * from './tutorial';

export type Option = {
  value: string;
  label: string;
  isSelected?: boolean;
};

export * from './deviceType';
export * from './engagement';

export const StickyPosition = {
  Sidebar: 'sidebar',
  Footer: 'footer',
} as const;
export type StickyPosition = (typeof StickyPosition)[keyof typeof StickyPosition];

export type ImageFile = File & { preview: string };

export const ArticleSort = {
  Title: 'title',
  Date: 'date',
} as const;
export type ArticleSort = (typeof ArticleSort)[keyof typeof ArticleSort];

export const ArticleOrder = {
  Asc: 'asc',
  Desc: 'desc',
} as const;
export type ArticleOrder = (typeof ArticleOrder)[keyof typeof ArticleOrder];

export const TagsMatchMode = {
  All: 'all',
  Any: 'any',
} as const;
export type TagsMatchMode = (typeof TagsMatchMode)[keyof typeof TagsMatchMode];

export const SortOrder = {
  Newest: 'newest',
  Oldest: 'oldest',
} as const;
export type SortOrder = (typeof SortOrder)[keyof typeof SortOrder];

export const ArticleVariant = {
  // 'default' renders the image-overlay card (photo fills the tile, date +
  // headline over a gradient scrim). 'stacked' is the design library's
  // image-above-text card; 'text-side-by-side' is the side/list layout.
  Default: 'default',
  Stacked: 'stacked',
  TextSideBySide: 'text-side-by-side',
} as const;
export type ArticleVariant = (typeof ArticleVariant)[keyof typeof ArticleVariant];

// How a card's image fits its frame (CSS object-fit). Backs the article/media
// cards' "Image fit" CMS control so editors can pick crop vs. show-whole-image.
export type ImageObjectFit = 'cover' | 'contain' | 'fill' | 'none';

// Predefined card width for the "Card size" CMS control. `auto` = today's
// fit-to-container behaviour; the named sizes map to fixed widths (height flows
// from the 16:9 image + text) defined once in themes' Common/_card-sizes.scss.
export type CardSize = 'auto' | 'small' | 'medium' | 'large' | 'xlarge';

// Media kind for the shared MediaCard (a MediaType-aware clone of ArticleCard).
export const MediaType = {
  Article: 'article',
  Video: 'video',
  Album: 'album',
} as const;
export type MediaType = (typeof MediaType)[keyof typeof MediaType];

export type Pagination = {
  currentPage: number;
  totalItems: number;
  totalPages: number;
  hasNextPage: boolean;
};

export interface PaginatedResponse<T> {
  items: T[];
  pagination: Pagination;
}

/** The Engagement Hub listing: one page of tiles, and the season they belong to. */
export interface EngagementHubResponse extends PaginatedResponse<EngagementTeaserPublic>, EngagementHubMeta {}
